import React, {memo, useCallback, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {PricingBreakdown} from '@findhotel/sapi'
import {Histogram} from 'components/Histogram'
import {usePriceFilter} from 'hooks/usePriceFilter'
import {
  getCurrencyCode,
  getCurrencyExchangeRate,
  getIsRtlLanguage
} from 'modules/meta/selectors'
import {getFacets, getPriceBucketWidth} from 'modules/sapiSearch/selectors'
import {PRICE_BUCKETS_COUNT} from 'modules/search/constants'
import {FilterUrlParams} from 'modules/search/types'
import {prop} from 'ramda'
import {Handle, HandleProps, Range} from 'rc-slider'

import {Icon} from '@vio/atlas/Icon'
import {getEnabledCurrencies} from '@vio/core/src/localization/business/currency'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {PriceInput} from './PriceInput/PriceInput'
import {
  generateContinuousPriceBuckets,
  getFirstBucketId
} from './PriceInput/utils'
import {
  InputsSeparator,
  InputsWrapper,
  InputWrapper,
  RangeSliderStylesProvider,
  RangeSliderWrapper,
  SliderWrapper
} from './styles'

interface Props {
  onChange: (filters: Pick<FilterUrlParams, 'priceMin' | 'priceMax'>) => void
  priceMax: number | null | undefined
  priceMin: number | null | undefined
}

const MIN_MAX_SLIDER_DISTANCE = 2

const PRICE_BUCKET_NAME: PricingBreakdown = 'medianRateBkt'
const PRICE_BUCKET_FACET = `pricing.${PRICE_BUCKET_NAME}`

const PriceFilterComponent = ({priceMin, priceMax, onChange}: Props) => {
  const {formatMessage} = useIntl()
  const {getNightlyPriceFilter, getTotalPriceFilter} = usePriceFilter()
  const facets = useSelector(getFacets) || {}
  const priceBucketWidth = useSelector(getPriceBucketWidth)
  const currencyCode = useSelector(getCurrencyCode)
  const enabledCurrencies = getEnabledCurrencies(
    process.env.NEXT_PUBLIC_ENABLED_CURRENCIES
  )
  const currency = enabledCurrencies.find(
    currency => currency.code === currencyCode
  )
  const currencySymbol = prop('symbol', currency)
  const currencyExchangeRate = useSelector(getCurrencyExchangeRate)

  const [[minFilter, maxFilter], setFilterValue] = useState<
    Array<number | null | undefined>
  >([getTotalPriceFilter(priceMin), getTotalPriceFilter(priceMax)])

  const priceBuckets = facets[PRICE_BUCKET_FACET] ?? []
  const continuousPriceBuckets = generateContinuousPriceBuckets(
    priceBuckets,
    PRICE_BUCKETS_COUNT
  )
  const firstBucketId = getFirstBucketId(continuousPriceBuckets)

  const priceBucketWidthInCurrency = getTotalPriceFilter(
    Math.round(priceBucketWidth * currencyExchangeRate)
  )
  const lowerBound = Math.round(firstBucketId * priceBucketWidthInCurrency)

  const upperBound = Math.round(
    lowerBound +
      priceBucketWidthInCurrency * (PRICE_BUCKETS_COUNT - firstBucketId)
  )

  // When opening the overlay, the first render has no values
  useEffect(() => {
    const min = getTotalPriceFilter(priceMin)
    const max = getTotalPriceFilter(priceMax)
    setFilterValue([min, max])
  }, [priceMin, priceMax, getTotalPriceFilter])

  const isRtlLanguage = useSelector(getIsRtlLanguage)

  const handlePriceChange = useCallback(
    (minValue: number, maxValue: number) => {
      const priceMin = getNightlyPriceFilter(
        minValue != lowerBound ? minValue : undefined
      )
      const priceMax = getNightlyPriceFilter(
        maxValue != upperBound ? maxValue : undefined
      )

      onChange({priceMin, priceMax})
    },
    [lowerBound, upperBound, onChange, getNightlyPriceFilter]
  )

  const handleSubmit = useCallback(() => {
    handlePriceChange(minFilter, maxFilter)
  }, [maxFilter, minFilter, handlePriceChange])

  const handleRangeChange = useCallback((value: number[]) => {
    const [min, max] = value

    setFilterValue([min, max])
  }, [])

  const handleInputChange = useCallback(
    (name: string, value: number) => {
      const rangeValue =
        name === 'priceMin'
          ? [Number(value), getTotalPriceFilter(priceMax) || upperBound]
          : [getTotalPriceFilter(priceMin) || lowerBound, Number(value)]

      setFilterValue(rangeValue)

      const [priceMinValue, priceMaxValue] = rangeValue

      handlePriceChange(priceMinValue, priceMaxValue)
    },
    [
      priceMax,
      priceMin,
      lowerBound,
      upperBound,
      handlePriceChange,
      getTotalPriceFilter
    ]
  )

  if (!facets)
    return (
      <FormattedMessageWrapper
        id="noFiltersAvailable"
        defaultMessage="No filters available for your search"
      />
    )

  const priceMinValue = minFilter || lowerBound
  const priceMaxValue = maxFilter || upperBound

  const priceMinLowerBound = lowerBound
  const priceMaxUpperBound = upperBound

  const priceRangeValue = [priceMinValue, priceMaxValue]
  const priceMinUpperBound = priceMaxValue - priceBucketWidthInCurrency
  const priceMaxLowerBound = priceMinValue + priceBucketWidthInCurrency

  // We should not show the last bucket on the histogram
  const histogramValues = continuousPriceBuckets.slice(0, -1)
  const histogramValue = [
    priceMinValue,
    priceMaxValue - priceBucketWidthInCurrency
  ]

  return (
    <>
      <SliderWrapper>
        <Histogram
          values={histogramValues}
          value={histogramValue}
          step={priceBucketWidthInCurrency}
          height="38px"
        />
        <RangeSliderWrapper data-body-scroll-lock-ignore>
          <RangeSliderStylesProvider>
            <Range
              min={lowerBound}
              max={upperBound}
              value={priceRangeValue}
              pushable={priceBucketWidth * MIN_MAX_SLIDER_DISTANCE}
              allowCross={false}
              step={priceBucketWidthInCurrency}
              reverse={isRtlLanguage}
              handle={(handleProps: HandleProps) => (
                <Handle {...handleProps}>
                  <Icon name="Drag" size="xs" />
                </Handle>
              )}
              onChange={handleRangeChange}
              onAfterChange={handleSubmit}
            />
          </RangeSliderStylesProvider>
        </RangeSliderWrapper>
      </SliderWrapper>
      <InputsWrapper>
        <InputWrapper>
          <PriceInput
            name="priceMin"
            label={formatMessage({id: 'min', defaultMessage: 'Min'})}
            value={priceMinValue}
            currencySymbol={currencySymbol}
            lowerBound={priceMinLowerBound}
            upperBound={priceMinUpperBound}
            onChange={handleInputChange}
          />
        </InputWrapper>
        <InputsSeparator>-</InputsSeparator>
        <InputWrapper>
          <PriceInput
            name="priceMax"
            label={formatMessage({id: 'max', defaultMessage: 'Max'})}
            value={priceMaxValue}
            currencySymbol={currencySymbol}
            lowerBound={priceMaxLowerBound}
            upperBound={priceMaxUpperBound}
            onChange={handleInputChange}
          />
        </InputWrapper>
      </InputsWrapper>
    </>
  )
}

export const PriceFilter = memo(PriceFilterComponent)
